import { NODE_LABELS, isDbRegistryDatabaseUrn, type NodeLabel } from '@atbash-labs/ontology-core'
import {
  plantAreaHref,
  plantComputeHostHref,
  plantComputeTagProviderHref,
  plantEntityHref,
  plantEquipmentItemHref,
  plantLineHref,
  plantLineHrefFromLineId,
  plantPlcNodeHref,
  plantDcsNodeHref,
  plantNetworkNodeHref,
  plantProcessHref,
  plantQueryHref,
  plantRoboticsNodeHref,
  plantSchedulesNodeHref,
  plantProtectiveFunctionHref,
  plantLogicSolverHref,
  plantFunctionLogicDiagramHref,
  plantSafetyPointHref,
  plantSrsFunctionGroupHref,
  plantSiteHref,
  plantStrayLineHref
} from './plant-view-paths'
import { tagsTagHref } from './tag-links'
import { splitTagProviderId } from './plantview-urns'

export type PlantSurface =
  | { kind: 'bespoke'; href: (id: string, context?: PlantNodeHrefContext) => string | null }
  | { kind: 'generic' }

export type PlantNodeHrefContext = Readonly<object>

const generic = { kind: 'generic' } as const
const bespoke = (
  href: (id: string, context?: PlantNodeHrefContext) => string | null
): PlantSurface => ({ kind: 'bespoke', href })
const tagProviderHref = (id: string): string | null => {
  const parts = splitTagProviderId(id)
  return parts ? plantComputeTagProviderHref(parts.computeId, parts.providerId) : null
}
const lineHref = (id: string, context?: PlantNodeHrefContext): string => {
  if (readContextValue(context, 'stray') === true) return plantStrayLineHref(id)
  const siteId =
    readString(readContextValue(context, 'siteId')) ??
    readString(readContextValue(context, 'site_id'))
  return siteId ? plantLineHref(siteId, id) : plantLineHrefFromLineId(id)
}
const databaseHref = (id: string): string | null => {
  // Registry / dump catalogs (`urn:db:<name>`) are Compute-tab hosts.
  // Ignition-serdes catalogs (`…/db:<name>` under a Compute) stay on the
  // generic entity page so the parent host remains the navigation target.
  return isDbRegistryDatabaseUrn(id) ? plantComputeHostHref(id) : null
}
const tagHref = (id: string): string => tagsTagHref(id)

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function readContextValue(context: PlantNodeHrefContext | undefined, key: string): unknown {
  return context ? (context as Record<string, unknown>)[key] : undefined
}

const GENERIC_SURFACE_BY_LABEL: Record<NodeLabel, PlantSurface> = Object.fromEntries(
  NODE_LABELS.map((label) => [label, generic])
) as Record<NodeLabel, PlantSurface>

const BESPOKE_SURFACE_BY_LABEL = {
  Compute: bespoke(plantComputeHostHref),
  TagProvider: bespoke(tagProviderHref),
  Tag: bespoke(tagHref),
  Site: bespoke(plantSiteHref),
  Controller: bespoke(plantPlcNodeHref),
  Program: bespoke(plantPlcNodeHref),
  Routine: bespoke(plantPlcNodeHref),
  AOI: bespoke(plantPlcNodeHref),
  PLCDataType: bespoke(plantPlcNodeHref),
  PLCDataTypeMember: bespoke(plantPlcNodeHref),
  PLCTag: bespoke(plantPlcNodeHref),
  DcsController: bespoke(plantDcsNodeHref),
  Cee: bespoke(plantDcsNodeHref),
  ControlModule: bespoke(plantDcsNodeHref),
  FunctionBlock: bespoke(plantDcsNodeHref),
  IoModule: bespoke(plantDcsNodeHref),
  IoChannel: bespoke(plantDcsNodeHref),
  ControlTag: bespoke(plantDcsNodeHref),
  ControlAlarm: bespoke(plantDcsNodeHref),
  ControlSetpoint: bespoke(plantDcsNodeHref),
  Asset: bespoke(plantNetworkNodeHref),
  NetworkSegment: bespoke(plantNetworkNodeHref),
  NetworkDevice: bespoke(plantNetworkNodeHref),
  Process: bespoke(plantProcessHref),
  Line: bespoke(lineHref),
  Equipment: bespoke(plantEquipmentItemHref),
  RobotController: bespoke(plantRoboticsNodeHref),
  RobotArm: bespoke(plantRoboticsNodeHref),
  RobotJob: bespoke(plantRoboticsNodeHref),
  RobotIOSignal: bespoke(plantRoboticsNodeHref),
  RobotCondition: bespoke(plantRoboticsNodeHref),
  VisionProcess: bespoke(plantRoboticsNodeHref),
  VisionData: bespoke(plantRoboticsNodeHref),
  Area: bespoke(plantAreaHref),
  NamedQuery: bespoke(plantQueryHref),
  Database: bespoke(databaseHref),
  ProtectiveFunction: bespoke(plantProtectiveFunctionHref),
  SrsSection: bespoke((id) => plantSrsFunctionGroupHref(id)),
  LogicSolver: bespoke(plantLogicSolverHref),
  FunctionLogicDiagram: bespoke(plantFunctionLogicDiagramHref),
  SafetyPoint: bespoke(plantSafetyPointHref),
  ScheduleProfile: bespoke(plantSchedulesNodeHref),
  Holiday: bespoke(plantSchedulesNodeHref)
} satisfies Partial<Record<NodeLabel, PlantSurface>>

export const PLANT_SURFACE_BY_LABEL: Readonly<Record<NodeLabel, PlantSurface>> = {
  ...GENERIC_SURFACE_BY_LABEL,
  ...BESPOKE_SURFACE_BY_LABEL
}

function plantSurfaceHref(
  label: NodeLabel,
  id: string,
  context?: PlantNodeHrefContext
): string | null {
  const surface = PLANT_SURFACE_BY_LABEL[label]
  return surface.kind === 'bespoke' ? surface.href(id, context) : null
}

/** Resolve every registered node label to its canonical bespoke or generic page. */
function plantNodeHref(label: NodeLabel, id: string, context?: PlantNodeHrefContext): string {
  return plantSurfaceHref(label, id, context) ?? plantEntityHref(id)
}

/**
 * Default plant navigation for an ontology node.
 *
 * - **id only** → `/plant/entity/:id` (resolver redirects to bespoke or
 *   renders the generic page).
 * - **label present** → surface registry fast-path (skip the resolver hop
 *   when the caller already knows the type).
 */
export function plantHref(id: string, label?: NodeLabel, context?: PlantNodeHrefContext): string {
  if (label) return plantNodeHref(label, id, context)
  return plantEntityHref(id)
}
